import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Permissions } from '@tcr-erp/shared';
import { b2bReceivableKeys } from '@/services/queryKeys';
import { b2bReceivableService } from '@/services/b2b-receivable.service';
import { useRbac } from './useRbac';

/**
 * TCR-1522 — danh sách `customer_id` khách B2B tổ chức ĐANG vượt hạn mức
 * công nợ (real-time), dùng để vẽ badge "Quá công nợ" trên trang danh sách
 * đơn (`useOrderColumns.tsx`) mà KHÔNG phải gọi `useB2BCreditLimit()` (số ít)
 * N+1 lần theo từng đơn/khách trên 1 trang danh sách.
 *
 * Endpoint riêng chỉ trả các `customer_id` vượt hạn mức và tự scope theo role
 * (owner/manager thấy toàn tenant, NVKD chỉ thấy khách gắn với mình). Không dùng
 * endpoint `credit-limits` vì danh sách đó đã phân trang và có thể bỏ sót badge.
 *
 * `is_over_limit` (field BE mới thêm — xem b2b-receivable.service.ts trên
 * repo BE) tính theo current_debt so với credit_limit THẬT — KHÁC hẳn
 * `status`/`ratio` (so nợ với avg_order_3months, dùng cho bảng "Hạn mức nợ"
 * ở trang Receivables) — không được lẫn lộn 2 khái niệm.
 *
 * `staleTime` ngắn (30s) vì badge cần phản ánh real-time: 1 đơn mới tạo có
 * thể đẩy khách vượt hạn mức ngay lập tức (PosOrderService tính lại mỗi lần
 * tạo đơn B2B tổ chức — xem TCR-1522 Task 5).
 */
export function useB2BCreditOverLimitCustomers() {
  const { hasPermission } = useRbac();
  // Chỉ role có quyền xem AR (owner/manager/accountant/b2b_sales_manager/
  // b2b_sales — xem READ_ROLES ở B2BReceivableController) mới gọi API này —
  // tránh bắn request chắc chắn 403 cho các role khác (vd kho, thu ngân) chỉ
  // để lấy 1 cái badge phụ trên danh sách đơn.
  const canViewReceivables = hasPermission(Permissions.B2B_RECEIVABLES_VIEW);

  const query = useQuery<string[], unknown, Set<string>>({
    queryKey: b2bReceivableKeys.overLimitCustomerIds(),
    queryFn: b2bReceivableService.overLimitCustomerIds,
    enabled: canViewReceivables,
    staleTime: 30_000,
    select: (customerIds) => new Set(customerIds),
  });

  // Fallback rỗng khi query chưa có data (loading/disabled/error) — component
  // gọi hook này luôn nhận được 1 Set hợp lệ, không phải check undefined.
  return useMemo(() => query.data ?? new Set<string>(), [query.data]);
}
